import { IBvTableSort } from 'volta/components/bv-table';
// @ts-ignore
import ActionCell from 'volta/components/cell/action-cell';
// @ts-ignore
import BufferIconCell from 'volta/components/cell/buffer-icon-cell';
// @ts-ignore
import CodeDescCell from 'volta/components/cell/code-desc-cell';
import AbstractTransactionsCopier, {
  IAbstractTransactionsCopierArgs,
  WAREHOUSE_ITEM_SEARCH_QUERY
} from 'volta/components/transaction-copier';
import SkuModel from 'volta/models/sku';
import { TransactionResourceName } from 'volta/models/transaction';
import Warehouse from 'volta/models/warehouse';

import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import { tracked } from '@glimmer/tracking';

interface IArgs extends IAbstractTransactionsCopierArgs {}

export default class TransactionsCopyToModal extends AbstractTransactionsCopier<IArgs> {
  // Properties
  // ~~~~~

  @tracked itemSearchQueryParams = this.args.itemSearchQueryParams ?? WAREHOUSE_ITEM_SEARCH_QUERY;
  @tracked selectedWarehouseId = this.args.selectedWarehouseId;
  @tracked selectedWarehouse?: Warehouse;
  @tracked pickedItems?: SkuModel[] = [];
  @tracked selectedItemsSorts: IBvTableSort[] = [{ valuePath: 'part.code', isAscending: true }];

  resourceName = TransactionResourceName;

  get selectedItems(): SkuModel[] {
    return this.pickedItems ?? [];
  }

  @sort('selectedItems', 'selectedItemsSortProperties')
  sortedItems!: SkuModel[];

  // Computed Properties
  // ~~~~~

  get selectedItemsSortProperties() {
    return this.selectedItemsSorts.map((s) => {
      return `${s.valuePath}:${s.isAscending ? 'asc' : 'desc'}`;
    });
  }

  get selectedItemsColumns() {
    const { intl } = this;
    return [
      {
        valuePath: 'delete',
        width: 40,
        isReorderable: false,
        isResizable: false,
        isSortable: false,
        cellComponent: ActionCell,
        format: (model: SkuModel) => {
          return {
            model,
            context: this,
            visible: true,
            action: this.removeItem,
            icon: 'trash',
            color: 'subtle',
            size: 'sm',
            tooltip: intl.t('delete')
          };
        }
      },
      {
        columnName: intl.t('warehouse'),
        valuePath: 'warehouse.code',
        width: 100,
        isReorderable: true,
        isResizable: true,
        cellComponent: CodeDescCell,
        format: (model: SkuModel) => {
          const { code, description } = model.warehouse;
          return {
            code,
            description
          };
        }
      },
      {
        columnName: intl.t('part'),
        valuePath: 'part.code',
        width: 250,
        isReorderable: true,
        isResizable: true,
        cellComponent: CodeDescCell,
        format: (model: SkuModel) => {
          const {
            part: { code },
            skuDescription: description
          } = model;
          return {
            code,
            description
          };
        }
      },
      {
        columnName: intl.t('planningMethodology'),
        valuePath: 'methodology',
        sortKey: 'methodology',
        width: 60,
        cellComponent: BufferIconCell,
        isReorderable: true,
        isResizable: true
      },
      {
        columnName: intl.t('acqCode'),
        sortKey: 'acqCode',
        valuePath: 'acqCode',
        width: 80,
        isReorderable: true,
        isResizable: true
      },
      {
        columnName: intl.t('leadTimes'),
        sortKey: 'voltaLeadTime',
        valuePath: 'voltaLeadTime',
        width: 80,
        textAlign: 'right',
        classNames: 'u-text-mono',
        isReorderable: true,
        isResizable: true,
        format: (model: SkuModel) => {
          const { leadTime, voltaLeadTime } = model;
          return (voltaLeadTime || leadTime) + ' / ' + (leadTime || 'NA');
        }
      },
      {
        columnName: intl.t('inventoryUnitPrice'),
        sortKey: 'inventoryUnitPrice',
        valuePath: 'inventoryUnitPrice',
        width: 80,
        textAlign: 'right',
        classNames: 'u-text-mono',
        isReorderable: true,
        isResizable: true,
        format: (model: SkuModel) => {
          const { inventoryUnitPrice } = model;

          return inventoryUnitPrice ? intl.formatNumber(inventoryUnitPrice) : null;
        }
      }
    ];
  }

  // Actions
  // ~~~~~

  @action
  addItem(item: SkuModel) {
    this.bvMetrics.trackAction(this, 'addItem');

    if (item) {
      (this.pickedItems ?? []).addObject(item);
    }
  }

  @action
  removeItem(item: SkuModel) {
    this.bvMetrics.trackAction(this, 'removeItem');
    (this.pickedItems ?? []).removeObject(item);
  }

  @action
  sortTable(sorts: IBvTableSort[]) {
    this.bvMetrics.trackAction(this, 'sortTable');
    this.selectedItemsSorts = sorts;
  }
}
